import {
  createElement,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import axiosClient from "@service/axiosClient";
import { AgGridReact } from "ag-grid-react";
import { GridApi, GridReadyEvent, ITextFilterParams } from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import { FiSearch, FiEdit } from "react-icons/fi";
import { LuFileClock } from "react-icons/lu";
import { RiMoneyDollarCircleLine, RiBankLine } from "react-icons/ri";
import { MdEmail, MdOutlinePendingActions } from "react-icons/md";
import { FaBriefcaseMedical } from "react-icons/fa";

import ContractorUpdateModal from "@components/contractor/ContractorUpdateModal";
import WorkerBankInfoModal from "@components/contractor/WorkerBankInfoModal";
import { IContractorDTO } from "@interfaces/IContractorDTO";
import { Box } from "@mui/material";
import Input from "@components/Input";
import { PtoEdit } from "@components/pto_dash/PtoEdit";
import { RatesEdit } from "@components/rates/RatesEdit";
import { ContractorListMobile } from "@components/contractor/ContractorListMobile";

import ContractorPayrollAdditions from "@components/contractor/ContractorPayrollAdditionsModal";
import ContractorEngagementStatusModal from "@components/contractor/ContractorEngagementStatusModal";
import ContractorRatesManageModal from "@components/contractor/ContractorRatesManageModal";
import { SendRegistrationEmailModal } from "@components/contractor/SendRegistrationEmailModal";
import { formatContractorName } from "@utils/nameUtils";
import { AgGridContextMenu } from "@components/ag_grid/AgGridContextMenu";
import { SnackbarContext } from "@components/constant_components/providers/SnackbarProvider";
import { useAuth } from "@components/context/AuthContext";
import { PTORequestModal } from "@components/user_pto/PTORequestModal";
import { createPtoRequests } from "@service/ptoService";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
} from "date-fns";

export function ContractorList(): JSX.Element {
  const { getInfo, getIsPayrollAdmin } = useAuth();
  const [isPayrollAdmin, setIsPayrollAdmin] = useState(false);
  const { triggerSnackbar } = useContext(SnackbarContext);
  const [userType, setUserType] = useState("");
  const [contractors, setContractors] = useState<IContractorDTO[]>([]);
  const [worker_id, setWorker_id] = useState<number>(0);
  const [contractor_id, setContractor_id] = useState<number>(0);
  const [contractor_name, setContractor_name] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [showPTODialog, setShowPTODialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showRatesDialog, setShowRatesDialog] = useState(false);
  const [showAdditionsDialog, setShowAdditionsDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showRatesManageDialog, setShowRatesManageDialog] = useState(false);
  const [showBankInfoDialog, setShowBankInfoDialog] = useState(false);
  const [showSickLeaveModal, setShowSickLeaveModal] = useState(false);

  const [sickLeaveCurrentMonth, setSickLeaveCurrentMonth] = useState(
    () => new Date()
  );
  const [sickLeaveDescription, setSickLeaveDescription] = useState("");
  const [sickLeaveSelection, setSickLeaveSelection] = useState<
    Record<string, string>
  >({});
  const [sickLeaveSubmitError, setSickLeaveSubmitError] = useState<
    string | null
  >(null);
  const [showSuccessAnim, setShowSuccessAnim] = useState(false);

  const breakpoint = 768; // Define the breakpoint for mobile view
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  const [active_status, setActiveStatus] = useState(1); // 1 = show active, 0 = show inactive, 2 = show all
  const [selectedContractor, setSelectedContractor] =
    useState<IContractorDTO | null>(null);
  const [filter, setFilter] = useState<any>({
    is_active: {
      type: "equals",
      filter: "true",
      filterType: "text",
    },
  });

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  useEffect(() => {
    async function fetchUserInfo() {
      const result = await getInfo();
      if (result.userType) {
        setUserType(result.userType);
      }
      const isAdmin = await getIsPayrollAdmin();
      if (!isAdmin) {
        setIsPayrollAdmin(false);
      } else {
        setIsPayrollAdmin(isAdmin);
      }
    }

    fetchUserInfo();
  }, [getInfo, getIsPayrollAdmin]);

  useEffect(() => {
    async function fetchContractors() {
      setIsLoading(false);
      try {
        const response = await axiosClient.get(
          "/worker_api/contractors/by_plants"
        );
        setContractors(response.data);
      } catch (error) {
        console.error("Error fetching contractors:", error);
      }
    }
    if (isLoading) {
      fetchContractors();
    }
  }, [isLoading]);

  const [gridApi, setGridApi] = useState<GridApi>();
  const defaultColDef = {
    sortable: true,
  };
  const [searchVal, setsearchVal] = useState("");

  // Context menu hook
  const { handleCellContextMenu, ContextMenuComponent } = AgGridContextMenu({});

  // Prevent default browser context menu
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  function onGridReady(params: GridReadyEvent): void {
    setGridApi(params.api);
    // Remove filter logic from here
  }

  const onRowClicked = (event: any) => {
    setSelectedContractor(event.data);
  };

  // Set filter only on first render when gridApi is set
  useEffect(() => {
    if (gridApi) {
      gridApi.setFilterModel(filter);
    }
    // Only run when gridApi is set for the first time
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridApi]);

  function onFilterChanged() {
    if (gridApi) {
      const currentFilterModel = gridApi.getFilterModel();
      setFilter(currentFilterModel);
    }
  }

  const updateSearchQuery = (val: string): void => {
    if (gridApi !== undefined) {
      gridApi.setQuickFilter(val);
      setsearchVal(val);
    }
  };

  const handlePTOClicked = (param: any): void => {
    if (param.is_active === true) {
      setContractor_name(formatContractorName(param));
      setContractor_id(param.contractor_id); // Assuming param has a property
      setShowPTODialog(true);
    } else {
      triggerSnackbar("Contractor is not active, cannot change PTO.");
    }
  };
  // const handleRatesClicked = (param: any): void => {
  //   if (param.is_active === true) {
  //     setContractor_name(formatContractorName(param));
  //     // Track contractor id as well for potential future use
  //     setContractor_id(param.contractor_id);
  //     setWorker_id(param.id); // Assuming param has a property
  //     setShowRatesDialog(true);
  //   } else {
  //     triggerSnackbar("Contractor is not active, cannot change Rates.");
  //   }
  // };
  const handleAdditionsClicked = (param: any): void => {
    if (param.is_active === true) {
      setContractor_name(formatContractorName(param));
      setWorker_id(param.id); // worker (internal) id
      setContractor_id(param.contractor_id); // external contractor id
      setShowAdditionsDialog(true);
    } else {
      triggerSnackbar("Contractor is not active, cannot change Additions.");
    }
  };

  const handleStatusClicked = (param: any): void => {
    setContractor_name(formatContractorName(param));
    setWorker_id(param.id);
    setContractor_id(param.contractor_id);
    setShowStatusDialog(true);
  };

  const handleRatesManageClicked = (param: any): void => {
    if (param.is_active === true) {
      setContractor_name(formatContractorName(param));
      setContractor_id(param.contractor_id);
      setWorker_id(param.id);
      setShowRatesManageDialog(true);
    } else {
      triggerSnackbar("Contractor is not active, cannot manage Rates.");
    }
  };

  const handleCloseRatesManage = () => {
    setShowRatesManageDialog(false);
    setContractor_name("");
    setWorker_id(0);
    setContractor_id(0);
  };

  const handleBankInfoClicked = (param: any): void => {
    if (param.is_active === true) {
      setContractor_name(formatContractorName(param));
      setWorker_id(param.id);
      setShowBankInfoDialog(true);
    } else {
      triggerSnackbar("Contractor is not active, cannot edit Bank Info.");
    }
  };

  const handleCloseBankInfo = () => {
    setShowBankInfoDialog(false);
    setContractor_name("");
    setWorker_id(0);
  };

  const handleClosePTO = () => {
    setShowPTODialog(false);
    setContractor_name("");
    setContractor_id(0);
  };

  const handleCloseRates = () => {
    setShowRatesDialog(false);
    setContractor_name("");
    setContractor_id(0);
  };

  const handleEdit = (param: any): void => {
    setWorker_id(param.id);
    setShowEditDialog(true);
  };

  const handleClose = () => {
    setShowEditDialog(false);
    setWorker_id(0);
    setIsLoading(true);
  };

  const handleRefresh = () => {
    setIsLoading(true);
  };

  const handleCloseAdditions = () => {
    setShowAdditionsDialog(false);
    setContractor_name("");
    setWorker_id(0);
  };

  const handleCloseStatus = () => {
    setShowStatusDialog(false);
    setContractor_name("");
    setWorker_id(0);
    setContractor_id(0);
  };

  const handleSendRegistrationEmail = (param: any): void => {
    if (param.is_active === true) {
      setContractor_name(formatContractorName(param));
      setWorker_id(param.id);
      setShowEmailDialog(true);
    } else {
      triggerSnackbar(
        "Contractor is not active, cannot send registration email."
      );
    }
  };

  const handleCloseEmail = () => {
    setShowEmailDialog(false);
    setContractor_name("");
    setWorker_id(0);
  };

  const handleSickLeaveClicked = (param: any): void => {
    if (param.is_active === true) {
      setContractor_name(formatContractorName(param));
      setWorker_id(param.id);
      setShowSickLeaveModal(true);
    } else {
      triggerSnackbar("Contractor is not active, cannot request sick leave.");
    }
  };

  const handleCloseSickLeave = () => {
    setShowSickLeaveModal(false);
    setContractor_name("");
    setWorker_id(0);
    setSickLeaveSelection({});
    setSickLeaveDescription("");
    setSickLeaveSubmitError(null);
  };

  const handlePrevSickMonth = () =>
    setSickLeaveCurrentMonth((d) => addMonths(d, -1));
  const handleNextSickMonth = () =>
    setSickLeaveCurrentMonth((d) => addMonths(d, 1));

  const sickLeaveMonthMatrix = useMemo(() => {
    const start = startOfWeek(startOfMonth(sickLeaveCurrentMonth), {
      weekStartsOn: 0,
    });
    const end = endOfWeek(endOfMonth(sickLeaveCurrentMonth), {
      weekStartsOn: 0,
    });
    const days = [];
    let day = start;
    while (day <= end) {
      days.push(day);
      day = addDays(day, 1);
    }
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    return weeks;
  }, [sickLeaveCurrentMonth]);

  const toggleSickLeave = (dateStr: string) => {
    setSickLeaveSelection((prev) => {
      const current = prev[dateStr];
      if (current === "FULL") {
        return { ...prev, [dateStr]: "AM" };
      }
      if (current === "AM") {
        const { [dateStr]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [dateStr]: "FULL" };
    });
  };

  const onFinalizeSickLeave = useCallback(() => {
    setSickLeaveSubmitError(null);
    const selections = Object.entries(sickLeaveSelection).map(
      ([date, part]) => ({
        date,
        part,
      })
    );
    if (selections.length === 0) {
      setSickLeaveSubmitError("Please select at least one day.");
      triggerSnackbar("Please select at least one day.");
      return;
    }

    createPtoRequests({
      selections,
      description: sickLeaveDescription || "",
      worker_id: worker_id,
      request_type: "sick",
    })
      .then(() => {
        triggerSnackbar("Sick leave request submitted successfully.");
        setShowSuccessAnim(true);
        setTimeout(() => {
          setSickLeaveSelection({});
          setSickLeaveDescription("");
          setShowSickLeaveModal(false);
          setShowSuccessAnim(false);
        }, 800);
      })
      .catch((err: any) => {
        console.error(err);
        const message =
          err?.response?.data?.error ||
          err.message ||
          "Failed to submit request.";
        setSickLeaveSubmitError(message);
        triggerSnackbar(message);
      });
  }, [sickLeaveSelection, sickLeaveDescription, worker_id, triggerSnackbar]);

  const columnDefs = [
    {
      headerName: "Plant Name",
      field: "plant_name",
      filter: true,
    },
    {
      headerName: "Contractor Id",
      field: "contractor_id",
      width: 120,
      filter: true,
    },
    {
      headerName: "First Name",
      field: "first_name",
      width: 120,
      filter: true,
    },
    {
      headerName: "Second Name",
      field: "second_name",
      width: 120,
      filter: true,
    },
    {
      headerName: "Last Name",
      field: "third_name",
      width: 120,
      filter: true,
    },
    {
      headerName: "Second Last Name",
      field: "fourth_name",
      width: 150,
      filter: true,
    },
    {
      headerName: "PTO Vacation",
      field: "pto_vacation",
      width: 120,
      filter: true,
      valueFormatter: (params: any) => {
        if (params.value == null || Number(params.value) === 0) return "";
        return Number(params.value).toFixed(0).padStart(2, "0");
      },
    },
    {
      headerName: "PTO Sick",
      field: "pto_sick",
      width: 100,
      filter: true,
      valueFormatter: (params: any) => {
        if (params.value == null || Number(params.value) === 0) return "";
        return Number(params.value).toFixed(0).padStart(2, "0");
      },
    },
    ...(userType === "super_admin"
      ? [
          {
            headerName: "Bank Account",
            field: "bank_account",
            width: 140,
            filter: true,
            cellStyle: { textAlign: "center" },
            valueFormatter: (params: any) => {
              const v = params.value;
              if (!v || String(v).length < 4) return "";
              if (isPayrollAdmin) {
                return `****${String(v).slice(-4)}`;
              }
              return `********`;
            },
          },
          {
            headerName: "Routing Number",
            field: "routing_number",
            width: 150,
            filter: true,
            cellStyle: { textAlign: "center" },
            valueFormatter: (params: any) => {
              const v = params.value;
              if (!v || String(v).length < 4) return "";
              if (isPayrollAdmin) {
                return `****${String(v).slice(-4)}`;
              }
              return `********`;
            },
          },
        ]
      : []),
    {
      headerName: "Email",
      field: "email",
      width: 230,
      filter: true,
    },
    {
      headerName: "Phone",
      field: "phone",
      width: 130,
      filter: true,
    },
    {
      headerName: "Temp Id",
      field: "temp_number",
      width: 100,
      filter: true,
    },
    {
      headerName: "Status",
      field: "is_active",
      width: 100,
      filter: true,
      filterParams: {
        defaultOption: true,
      } as unknown as ITextFilterParams,
      cellRenderer: (params: any): JSX.Element => {
        return (
          <div className={params.value ? "active" : "inactive"}>
            {params.value ? "Active" : "Not Active"}
          </div>
        );
      },
    },
  ];

  const handleSelectActiveContractors = (param: number) => {
    setActiveStatus(param);
    if (param === 1) {
      // Active
      setFilter({
        is_active: { type: "equals", filter: "true", filterType: "text" },
      });
      if (gridApi) {
        gridApi.setFilterModel({
          is_active: { type: "equals", filter: "true", filterType: "text" },
        });
      }
    } else if (param === 0) {
      // Inactive
      setFilter({
        is_active: { type: "equals", filter: "false", filterType: "text" },
      });
      if (gridApi) {
        gridApi.setFilterModel({
          is_active: { type: "equals", filter: "false", filterType: "text" },
        });
      }
    } else {
      // All
      setFilter({});
      if (gridApi) {
        gridApi.setFilterModel({});
      }
    }
  };
  return (
    <Box
      sx={{
        padding: { xs: "0.5em", md: "1em" },
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      <div className="tw-p-2 md:tw-p-4 tw-bg-white tw-rounded-lg tw-shadow-md tw-max-w-full tw-overflow-x-hidden">
        <h1 className="tw-mb-2 md:tw-mb-4 tw-color-primary tw-text-base md:tw-text-lg">
          Roster
        </h1>
        <div className="tw-flex tw-gap-2 tw-mb-4">
          <div className="tw-flex-1">
            <Input
              icon={FiSearch}
              containerStyle={{ marginRight: 0, width: "100%" }}
              id="search"
              type="text"
              name="search"
              placeholder="search"
              onChange={(e: any) => updateSearchQuery(e.target.value)}
              disabled={false}
              value={searchVal}
            />
          </div>
          <div className="tw-flex tw-justify-end tw-items-center tw-mr-2">
            <select
              id="active-filter-select"
              className="tw-bg-white tw-text-blue-600 tw-p-2 tw-pl-4 tw-rounded-lg tw-shadow tw-text-sm tw-border tw-max-w-[120px]"
              value={active_status}
              onChange={(e) =>
                handleSelectActiveContractors(Number(e.target.value))
              }
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
              <option value={2}>All</option>
            </select>
          </div>
        </div>
        {!isLoading && (
          <>
            <div
              className="tw-mb-4 tw-flex tw-flex-col md:tw-flex-row tw-gap-2 md:tw-gap-4"
              onContextMenu={handleContextMenu}
            >
              <div className="tw-flex-1 tw-w-full">
                {!isMobile ? (
                  <div className="ag-theme-balham tw-h-[73dvh] tw-w-full">
                    <AgGridReact
                      columnDefs={columnDefs}
                      rowHeight={32}
                      rowData={contractors}
                      gridOptions={{}}
                      onCellContextMenu={handleCellContextMenu}
                      rowSelection="single"
                      defaultColDef={defaultColDef}
                      onGridReady={onGridReady}
                      onFilterChanged={onFilterChanged}
                      onRowClicked={onRowClicked}
                      colResizeDefault="shift"
                      animateRows
                      pivotPanelShow="always"
                    />
                  </div>
                ) : (
                  <ContractorListMobile
                    contractors={contractors}
                    searchVal={searchVal}
                    active_status={active_status}
                    userType={userType}
                    handleEdit={handleEdit}
                    handlePTOClicked={handlePTOClicked}
                    // handleRatesClicked={handleRatesClicked}
                    handleAdditionsClicked={handleAdditionsClicked}
                    handleStatusClicked={handleStatusClicked}
                    handleSendRegistrationEmail={handleSendRegistrationEmail}
                    handleSickLeaveClicked={handleSickLeaveClicked}
                    handleRatesManageClicked={handleRatesManageClicked}
                  />
                )}
              </div>
              {!isMobile && (
                <div className="tw-w-64 tw-bg-gray-50 tw-p-4 tw-rounded-lg tw-border tw-border-gray-200">
                  {selectedContractor ? (
                    <>
                      <h3 className="tw-text-lg tw-font-semibold tw-mb-4 tw-text-gray-800">
                        {formatContractorName(selectedContractor)}
                      </h3>
                      <div className="tw-flex tw-flex-col tw-gap-2">
                        {userType === "super_admin" && (
                          <>
                            <button
                              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded tw-text-sm hover:tw-bg-blue-50 hover:tw-border-blue-500 tw-transition"
                              onClick={() => handleEdit(selectedContractor)}
                            >
                              {createElement(
                                FiEdit as React.ComponentType<{
                                  size?: number;
                                }>,
                                { size: 16 }
                              )}
                              <span>Edit</span>
                            </button>
                            <button
                              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded tw-text-sm hover:tw-bg-blue-50 hover:tw-border-blue-500 tw-transition"
                              onClick={() =>
                                handlePTOClicked(selectedContractor)
                              }
                            >
                              {createElement(
                                LuFileClock as React.ComponentType<{
                                  size?: number;
                                }>,
                                { size: 16 }
                              )}
                              <span>PTO</span>
                            </button>
                            {/* <button
                              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded tw-text-sm hover:tw-bg-blue-50 hover:tw-border-blue-500 tw-transition"
                              onClick={() =>
                                handleRatesClicked(selectedContractor)
                              }
                            >
                              {createElement(
                                RiMoneyDollarCircleLine as React.ComponentType<{
                                  size?: number;
                                }>,
                                { size: 16 }
                              )}
                              <span>Rates</span>
                            </button> */}
                            <button
                              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded tw-text-sm hover:tw-bg-blue-50 hover:tw-border-blue-500 tw-transition"
                              onClick={() =>
                                handleRatesManageClicked(selectedContractor)
                              }
                            >
                              {createElement(
                                RiMoneyDollarCircleLine as React.ComponentType<{
                                  size?: number;
                                }>,
                                { size: 16 }
                              )}
                              <span>Rates</span>
                            </button>
                            <button
                              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded tw-text-sm hover:tw-bg-blue-50 hover:tw-border-blue-500 tw-transition"
                              onClick={() =>
                                handleAdditionsClicked(selectedContractor)
                              }
                            >
                              {createElement(
                                RiMoneyDollarCircleLine as React.ComponentType<{
                                  size?: number;
                                }>,
                                { size: 16 }
                              )}
                              <span>Payroll Additions</span>
                            </button>
                            <button
                              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded tw-text-sm hover:tw-bg-blue-50 hover:tw-border-blue-500 tw-transition"
                              onClick={() =>
                                handleSendRegistrationEmail(selectedContractor)
                              }
                            >
                              {createElement(
                                MdEmail as React.ComponentType<{
                                  size?: number;
                                }>,
                                { size: 16 }
                              )}
                              <span>Registration Email</span>
                            </button>
                            <button
                              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded tw-text-sm hover:tw-bg-blue-50 hover:tw-border-blue-500 tw-transition"
                              onClick={() =>
                                handleStatusClicked(selectedContractor)
                              }
                            >
                              {createElement(
                                MdOutlinePendingActions as React.ComponentType<{
                                  size?: number;
                                }>,
                                { size: 16 }
                              )}
                              <span>Status</span>
                            </button>
                            {isPayrollAdmin && (
                              <button
                                className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded tw-text-sm hover:tw-bg-blue-50 hover:tw-border-blue-500 tw-transition"
                                onClick={() =>
                                  handleBankInfoClicked(selectedContractor)
                                }
                              >
                                {createElement(
                                  RiBankLine as React.ComponentType<{
                                    size?: number;
                                  }>,
                                  { size: 16 }
                                )}
                                <span>Bank Info</span>
                              </button>
                            )}
                          </>
                        )}
                        <button
                          className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded tw-text-sm hover:tw-bg-blue-50 hover:tw-border-blue-500 tw-transition"
                          onClick={() =>
                            handleSickLeaveClicked(selectedContractor)
                          }
                        >
                          {createElement(
                            FaBriefcaseMedical as React.ComponentType<{
                              size?: number;
                            }>,
                            { size: 16 }
                          )}
                          <span>Sick Leave</span>
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="tw-text-gray-500 tw-text-sm tw-text-center">
                      Select a contractor to view actions
                    </div>
                  )}
                </div>
              )}
            </div>
            {showPTODialog && (
              <PtoEdit
                contractor_id={contractor_id}
                isOpen={showPTODialog}
                onClose={handleClosePTO}
                contractor_name={contractor_name}
              />
            )}
            {showRatesDialog && (
              <RatesEdit
                worker_id={worker_id}
                isOpen={showRatesDialog}
                onClose={handleCloseRates}
                contractor_name={contractor_name}
              />
            )}
            {showRatesManageDialog && (
              <ContractorRatesManageModal
                workerId={worker_id}
                contractorId={contractor_id}
                contractorName={contractor_name}
                isOpen={showRatesManageDialog}
                onClose={handleCloseRatesManage}
                onSaved={() => setIsLoading(true)}
              />
            )}
            {showEditDialog && (
              <ContractorUpdateModal
                workerId={worker_id}
                isOpen={showEditDialog}
                onClose={handleClose}
                onHandleRefresh={handleRefresh}
              />
            )}
            {showAdditionsDialog && (
              <ContractorPayrollAdditions
                workerId={worker_id}
                contractorId={contractor_id}
                isOpen={showAdditionsDialog}
                onClose={handleCloseAdditions}
                contractorName={contractor_name}
                onAdditionSaved={() => setShowAdditionsDialog(false)}
              />
            )}
            {showStatusDialog && (
              <ContractorEngagementStatusModal
                workerId={worker_id}
                contractorId={contractor_id}
                contractorName={contractor_name}
                isOpen={showStatusDialog}
                onClose={handleCloseStatus}
                onSaved={() => setIsLoading(true)}
              />
            )}
            {showBankInfoDialog && (
              <WorkerBankInfoModal
                workerId={worker_id}
                contractorName={contractor_name}
                isOpen={showBankInfoDialog}
                onClose={handleCloseBankInfo}
                onSaved={() => setIsLoading(true)}
              />
            )}
            {showEmailDialog && (
              <SendRegistrationEmailModal
                worker_id={worker_id}
                contractor_name={contractor_name}
                isOpen={showEmailDialog}
                onClose={handleCloseEmail}
              />
            )}
            {showSickLeaveModal && (
              <PTORequestModal
                isOpen={showSickLeaveModal}
                onClose={handleCloseSickLeave}
                currentMonth={sickLeaveCurrentMonth}
                onPrevMonth={handlePrevSickMonth}
                onNextMonth={handleNextSickMonth}
                monthMatrix={sickLeaveMonthMatrix}
                selection={sickLeaveSelection}
                onToggle={toggleSickLeave}
                blockedMap={{}}
                description={sickLeaveDescription}
                onDescriptionChange={setSickLeaveDescription}
                submitError={sickLeaveSubmitError}
                onFinalize={onFinalizeSickLeave}
                isPending={false}
                showSuccessAnim={showSuccessAnim}
                requestType="sick"
                userName={contractor_name}
              />
            )}
            {ContextMenuComponent}
          </>
        )}
      </div>
    </Box>
  );
}
